import { uniqueId } from 'lodash-es';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { FaMinusSquare, FaPlusSquare } from '@meronex/icons/fa';
import { Box } from '@apia/theme';
import { domPropsSelector, rtDebug } from '.';
import { useAppSelector } from '../../../../hooks/hooks';
import useDomProps from '../../../../store/selection/useDomProps';
import Cell from './Cell';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import useRenderDebug from '../../../../hooks/useRenderDebug';
import IconButton from '../../buttons/IconButton';
import store from '../../../../store';
import { responsiveTableActions } from '../../../../store/responsiveTableSlice';
import { TResponsiveTableCell, TResponsiveTableColumn } from './types';
import AdditionalColumnDefaultRenderer from './renderers/AdditionalColumnDefaultRenderer';
import StateCell from './StateCell';
import SeparatorCell from './SeparatorCell';
import DefaultRowRenderer from './renderers/DefaultRowRenderer';

interface IRow {
  rowIndex: number;
}

type TAdditionalCell = {
  cell: TResponsiveTableCell;
  column: TResponsiveTableColumn;
  columnIndex: number;
};

const Row = ({ rowIndex, ...props }: IRow) => {
  const { name, ...rest } = useResponsiveTableContext();

  const { columns, hasNonAdditionalFilters } = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return {
      columns: tableState?.columns ?? [],
      hasNonAdditionalFilters: tableState?.hasNonAdditionalFilters,
    };
  }, shallowEqual);
  const row = useAppSelector(
    (global) => global.responsiveTableSlice[name].rows[rowIndex],
    shallowEqual,
  );
  const additionalCells: TAdditionalCell[] = useAppSelector(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      return tableState.columns
        .map((current, index) =>
          current.showAsAdditional
            ? {
                index,
                column: current,
              }
            : null,
        )
        .filter(
          (
            current,
          ): current is {
            index: number;
            column: TResponsiveTableColumn;
          } => !!current,
        )
        .map((current) => {
          return {
            cell: tableState.rows[rowIndex]?.cells[current.index],
            column: current.column,
            columnIndex: current.index,
          };
        });
    },
    (currentValue, newValue) => {
      return currentValue.reduce<boolean>(
        (accumulatedBoolean, currentCell, cellIndex) => {
          return (
            accumulatedBoolean && shallowEqual(currentCell, newValue[cellIndex])
          );
        },
        true,
      );
    },
  );
  const { isDragging, isExpanded, shouldForbidSelection } = useAppSelector(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      return {
        isExpanded: tableState.expandedRows.includes(rowIndex),
        isDragging: tableState.draggingRow === rowIndex,
        shouldForbidSelection: tableState.rows[rowIndex]?.forbidSelection,
      };
    },
    shallowEqual,
  );

  const domProps = useDomProps(
    'tr',
    {
      isThisRowExpanded: isExpanded,
      rowIndex,
    },
    domPropsSelector(name),
  );

  const expandedRowDomProps = useDomProps(
    'tr',
    {
      isTheExpandedRow: true,
      rowIndex,
    },
    domPropsSelector(name),
  );

  useRenderDebug(rtDebug.row, 'Row.tsx', {
    rowIndex,
    ...rest,
    name,
    columns,
    ...props,
    domProps,
    additionalCells,
    isExpanded,
    shouldForbidSelection,
    expandedRowDomProps,
    row,
  });

  const handleExpandedState = React.useCallback(
    (ev: boolean | React.MouseEvent) => {
      const willBeExpanded = typeof ev === 'boolean' ? ev : !isExpanded;
      if (!willBeExpanded)
        store.dispatch(
          responsiveTableActions.update({
            tableName: name,
            expandedRows: store
              .getState()
              .responsiveTableSlice[
                name
              ].expandedRows.filter((current) => current !== rowIndex),
          }),
        );
      else
        store.dispatch(
          responsiveTableActions.update({
            tableName: name,
            expandedRows: [
              ...store.getState().responsiveTableSlice[name].expandedRows,
              rowIndex,
            ],
          }),
        );
    },
    [isExpanded, name, rowIndex],
  );

  const statesCount = useAppSelector((globalState) => {
    const state = globalState.responsiveTableSlice[name];
    return state?.statesColumns ?? 0;
  }, shallowEqual);

  const states = React.useMemo(
    () => (row.states ?? []).map((current) => ({ ...current, id: uniqueId() })),
    [row.states],
  );

  const { ref, ...additionalCellDomProps } = useDomProps(
    'additionalTd',
    { rowIndex },
    domPropsSelector(name),
  );

  const Renderer = React.useMemo(
    () => row.renderer ?? DefaultRowRenderer,
    [row.renderer],
  );

  return (
    <>
      <Renderer
        {...domProps}
        row={row}
        rowIndex={rowIndex}
        className={`responsiveTable__row ${row.isHidden ? 'hidden' : ''} ${
          domProps['aria-selected'] ? 'selected' : ''
        } ${shouldForbidSelection ? 'non-selectable' : ''} ${states
          .map((current) => current.className)
          .join(' ')} ${row.className ?? ''} ${
          row.separator ? 'row__separator' : ''
        } ${isDragging ? 'draggingRow' : ''}`}
        onKeyDown={React.useCallback(
          (ev: React.KeyboardEvent) => {
            if (ev.key.toLowerCase() === 'arrowleft' && ev.ctrlKey) {
              ev.preventDefault();
              ev.stopPropagation();
              handleExpandedState(false);
            }
            if (ev.key.toLowerCase() === 'arrowright' && ev.ctrlKey) {
              ev.preventDefault();
              ev.stopPropagation();
              handleExpandedState(true);
            }
          },
          [handleExpandedState],
        )}
      >
        {row.separator ? (
          <SeparatorCell
            colSpan={
              (additionalCells.length > 0 || hasNonAdditionalFilters ? 1 : 0) +
              statesCount.length +
              columns.filter((current) => !current.showAsAdditional).length
            }
            rowIndex={rowIndex}
          />
        ) : (
          <>
            {(additionalCells.length > 0 || hasNonAdditionalFilters) && (
              <td {...additionalCellDomProps}>
                <Box className="rowStatesList">
                  {additionalCells.length > 0 && (
                    <IconButton
                      {...window.getVariant('icon-outline')}
                      icon={isExpanded ? FaMinusSquare : FaPlusSquare}
                      aria-label={
                        isExpanded
                          ? window.LBL_COLLAPSE_ROW
                          : window.LBL_EXPAND_ROW
                      }
                      className="moreInformationButton"
                      onClick={handleExpandedState}
                    />
                  )}
                </Box>
              </td>
            )}
            {Array(statesCount.length)
              .fill('')
              .map((_, i) => {
                return (
                  <StateCell
                    key={states[i]?.id ?? i}
                    index={i}
                    state={states[i]}
                    rowIndex={rowIndex}
                  />
                );
              })}
            {columns
              .map((current, actualColumnIndex) => ({
                ...current,
                actualColumnIndex,
              }))
              .filter((current) => !current.showAsAdditional)
              .map((current, inTableColumnIndex) => (
                <Cell
                  key={current.name}
                  columnIndex={current.actualColumnIndex}
                  rowIndex={rowIndex}
                  inTableColumnIndex={inTableColumnIndex}
                />
              ))}
          </>
        )}
      </Renderer>
      {isExpanded && (
        <tr className="expandedRow" {...expandedRowDomProps}>
          <td
            colSpan={
              columns.length - additionalCells.length + states.length + 1
            }
          >
            <Box className="responsiveTable__additionalInfoContainer">
              {additionalCells.map((current) => {
                const { Renderer: ColumnRenderer } = current.column;
                if (ColumnRenderer)
                  return (
                    <ColumnRenderer
                      key={current.column.name}
                      cell={current.cell}
                      column={current.column}
                      row={row}
                    />
                  );
                return (
                  <AdditionalColumnDefaultRenderer
                    key={current.column.name}
                    cell={current.cell}
                    column={current.column}
                    row={row}
                  />
                );
              })}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};

export default React.memo(Row);
